/**
 * ReplStream — single contiguous terminal-style log of an investigation.
 *
 * Renders the prompt, the parsed anchor, the tool-call timeline as a
 * vertical stack, and the narrative analysis.
 *
 * Timeline behaviour:
 *   - status === 'streaming': the full stack of tool calls is visible
 *     inline (newest at bottom), text updates smoothly as `tool_call` and
 *     `tool_result` events arrive. The currently-pending tool shows a
 *     small pulsing dot + "streaming" label. No typewriter, no collapse —
 *     the user can see the agent's working set at all times.
 *   - status === 'done' | 'idle' | 'error': the stack collapses behind a
 *     "▶ N tool calls [· M events]" header (collapsed by default for
 *     compactness on long investigations). Click expands; click again
 *     collapses.
 *   - The narrative analysis block is ALWAYS expanded.
 *
 * Expand state is held in component useState — investigation-scoped only,
 * no localStorage persistence.
 */

import { Box, CircularProgress, Collapse, Stack, Typography } from '@mui/material';
import type { ReactElement } from 'react';
import { useState } from 'react';

import { NarrativeMarkdown } from '@/components/NarrativeMarkdown';
import type { TimelineEntry } from '@/components/InvestigationTimeline';

export type ReplStatus = 'idle' | 'streaming' | 'done' | 'error';

export interface ReplToolCall {
  id: string;
  tool_name: string;
  args_summary: string;
  status: 'pending' | 'ok' | 'error';
  duration_ms?: number;
  rows?: number;
  error?: string;
}

export interface ReplAnchor {
  from_iso: string;
  to_iso: string;
  services: string[];
}

export interface ReplStreamProps {
  prompt: string;
  anchor: ReplAnchor | null;
  timeline: ReplToolCall[];
  narrative: string;
  status: ReplStatus;
  /**
   * Verb shown on the prompt line ("> {actionLabel} {prompt}"). Defaults to
   * "investigate" for the original /investigate use case. The reviews
   * detail page (P10) passes "review" so the REPL reads "> review
   * org/repo#123: PR title" instead of "> investigate ...".
   */
  actionLabel?: string;
}

// ─── Shared adapter (also used by AnalysisPanel) ─────────────────────────────

function extractRowCount(resultPreview: string | null): number | undefined {
  if (!resultPreview) return undefined;
  try {
    const raw = JSON.parse(resultPreview) as Record<string, unknown>;
    const val = raw.rows ?? raw.events_count ?? raw.count ?? raw.total;
    if (typeof val === 'number') return val;
  } catch {
    // not valid JSON — ignore
  }
  return undefined;
}

/**
 * adaptToolCallsToTimeline — converts a persisted TimelineEntry[] (from
 * InvestigationDetail or AnalysisResult) into the ReplToolCall[] format
 * consumed by ReplStream. Exported so both InvestigationDetailPage and
 * AnalysisPanel can import the same util instead of duplicating it.
 */
export function adaptToolCallsToTimeline(timeline: TimelineEntry[]): ReplToolCall[] {
  return timeline.map((entry) => {
    const status: ReplToolCall['status'] =
      entry.error !== null
        ? 'error'
        : entry.result_preview === null
          ? 'pending'
          : 'ok';
    return {
      id: entry.call_id,
      tool_name: entry.tool,
      args_summary: JSON.stringify(entry.args ?? {}),
      status,
      duration_ms: entry.latency_ms ?? undefined,
      rows: extractRowCount(entry.result_preview),
      error: entry.error ?? undefined,
    };
  });
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatToolLine(t: ReplToolCall): string {
  const args = t.args_summary.length > 80 ? t.args_summary.slice(0, 77) + '…' : t.args_summary;
  if (t.status === 'pending') {
    return `[tool] ${t.tool_name} ${args}`;
  }
  const dur = t.duration_ms !== undefined ? `${(t.duration_ms / 1000).toFixed(1)}s` : '';
  const rows = t.rows !== undefined ? ` · ${t.rows}` : '';
  const mark = t.status === 'ok' ? '✓' : '✗';
  return `[tool] ${t.tool_name} ${args} ${mark} ${dur}${rows}`;
}

/** Total event count across all timeline entries that have rows set. */
function totalRows(timeline: ReplToolCall[]): number {
  return timeline.reduce((acc, t) => acc + (t.rows ?? 0), 0);
}

// ─── Tool line component ──────────────────────────────────────────────────────

/**
 * ToolLine — single tool-call entry in the stack. Renders the formatted
 * text directly (no typewriter — text just updates smoothly when the
 * `tool` prop changes from pending → ok/error). Pending tools get a
 * small pulsing dot + "streaming" label; error tools get a red "error"
 * tag. Used for every entry, whether streaming or finished.
 */
function ToolLine({ tool }: { tool: ReplToolCall }): ReactElement {
  const text = formatToolLine(tool);
  return (
    <Box
      sx={{
        py: 0.5,
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 12,
        color: 'text.primary',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Box sx={{ flex: 1, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        <Box component="span" sx={{ color: 'primary.main' }}>
          {text.slice(0, 6)}
        </Box>
        {text.slice(6)}
      </Box>
      {tool.status === 'pending' && (
        <Box
          sx={{
            color: 'warning.main',
            fontSize: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          streaming
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: 'warning.main',
              boxShadow: '0 0 4px rgba(255, 176, 0, 0.8)',
              animation: 'pulse 1.6s ease-in-out infinite',
            }}
          />
        </Box>
      )}
      {tool.status === 'error' && (
        <Box sx={{ color: 'error.main', fontSize: 10 }}>error</Box>
      )}
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ReplStream(props: ReplStreamProps): ReactElement {
  const { prompt, anchor, timeline, narrative, status, actionLabel = 'investigate' } = props;
  const [expanded, setExpanded] = useState(false);

  const isStreaming = status === 'streaming';
  const toolCount = timeline.length;
  const eventsTotal = totalRows(timeline);

  // Header label: "▸ N tool calls [· M events]". While streaming with zero
  // tools yet (the gap between submit and the first tool_call event), we
  // still render a header with "running…" so the loader is visible during
  // that dead-air window.
  const glyph = expanded ? '▼' : isStreaming ? '▸' : '▶';
  const eventsLabel = eventsTotal > 0 ? ` · ${eventsTotal} events` : '';
  let headerLabel: string | null;
  if (toolCount > 0) {
    headerLabel = `${glyph} ${toolCount} tool call${toolCount === 1 ? '' : 's'}${eventsLabel}`;
  } else if (isStreaming) {
    headerLabel = `${glyph} running…`;
  } else {
    headerLabel = null;
  }

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 3,
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 12,
        lineHeight: 1.6,
      }}
    >
      {/* Prompt line */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="flex-start"
        sx={{ py: 1, borderBottom: 1, borderColor: 'divider', borderStyle: 'dashed' }}
      >
        <Box sx={{ color: 'primary.main', fontWeight: 700, flex: 'none' }}>
          &gt; {actionLabel}
        </Box>
        <Box
          sx={{
            flex: 1,
            color: 'text.primary',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {prompt || (
            <Box component="span" sx={{ color: 'text.faintest' }}>
              (waiting for prompt)
            </Box>
          )}
        </Box>
      </Stack>

      {/* Anchor line */}
      {anchor && (
        <Box sx={{ py: 0.5, color: 'text.faintest', fontSize: 11 }}>
          <Box component="span" sx={{ color: 'primary.main' }}>
            anchor
          </Box>{' '}
          {anchor.from_iso} → {anchor.to_iso}
          {anchor.services.length > 0 && (
            <>
              {' '}
              ·{' '}
              <Box component="span" sx={{ color: 'primary.main' }}>
                services
              </Box>{' '}
              {anchor.services.join(', ')}
            </>
          )}
        </Box>
      )}

      {/* Tool-call section — collapsed by default. Rendered when there are
          tool calls to show OR when the investigation is streaming (so the
          running-loader is visible even during the initial gap before the
          first tool_call event lands). */}
      {(toolCount > 0 || isStreaming) && (
        <Box sx={{ mt: 0.5 }}>
          {/* Collapse header / toggle. While streaming, a small spinner sits
              inline next to the label as the page-level "task running"
              indicator (companion to each tool line's pulsing dot). */}
          <Box
            component="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-controls="repl-tool-list"
            sx={{
              background: 'none',
              border: 'none',
              p: 0,
              m: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: 12,
              color: 'primary.main',
              py: 0.5,
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {isStreaming && (
              <CircularProgress
                size={12}
                color="primary"
                aria-label="investigation running"
              />
            )}
            {headerLabel}
          </Box>

          {/* Tool stack. While streaming, render the full list inline
              (newest at bottom) so the user can see every step the agent
              takes as it happens — no click required, no typewriter.
              After streaming ends, fall back to the collapse behaviour
              (collapsed by default to keep long investigations compact;
              the header toggles it open).
              The overflowX wrapper keeps wide tool-call args (long JSON
              strings) from overflowing the viewport on mobile — the
              content scrolls inside the REPL box instead of the page. */}
          {isStreaming ? (
            <Box
              id="repl-tool-list"
              sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}
            >
              {timeline.map((tool) => (
                <ToolLine key={tool.id} tool={tool} />
              ))}
            </Box>
          ) : (
            <Collapse
              in={expanded}
              timeout={220}
              id="repl-tool-list"
              aria-hidden={!expanded}
              unmountOnExit
            >
              <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                {timeline.map((tool) => (
                  <ToolLine key={tool.id} tool={tool} />
                ))}
              </Box>
            </Collapse>
          )}
        </Box>
      )}

      {/* Analysis — always expanded */}
      {narrative && (
        <>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'primary.main',
              mt: 1.5,
              pt: 1,
              borderTop: 1,
              borderColor: 'divider',
              borderStyle: 'dashed',
            }}
          >
            analysis
          </Typography>
          <Box sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, mt: 0.5 }}>
            <NarrativeMarkdown text={narrative} />
          </Box>
        </>
      )}
    </Box>
  );
}
